import { type ApiResponse } from "apisauce"

import { request } from "../../services/api"
import { type UserInfo } from "../LoginPage/types/auth.types.ts"
import {
  type UIPreferencesPatch,
  type UIPreferencesResponse
} from "./redux/ui-preferences.types.ts"

export type UserInfoResponse = UserInfo

export const getUserInfo = (): Promise<ApiResponse<UserInfoResponse>> =>
  request.api.get<UserInfoResponse>("/user/account/me/")

// The shell loads ui-preferences on boot and Home reads them again as it
// mounts; within this window, for the same session and with no save in
// between, the second caller gets the first read instead of a new request.
const UI_PREFERENCES_REUSE_MS = 10_000

let recentRead: {
  session: string | undefined
  startedAt: number
  response: Promise<ApiResponse<UIPreferencesResponse>>
} | null = null

const currentSession = (): string | undefined =>
  (request.api.headers as Record<string, string> | undefined)?.Authorization

export const getUIPreferences = (): Promise<
  ApiResponse<UIPreferencesResponse>
> => {
  const session = currentSession()
  if (
    recentRead &&
    recentRead.session === session &&
    Date.now() - recentRead.startedAt < UI_PREFERENCES_REUSE_MS
  ) {
    return recentRead.response
  }

  const response = request.api.get<UIPreferencesResponse>(
    "/user/ui-preferences/"
  )
  const read = { session, startedAt: Date.now(), response }
  recentRead = read
  const forget = () => {
    if (recentRead === read) recentRead = null
  }
  response.then((result) => {
    if (!result?.ok) forget()
  }, forget)
  return response
}

export const patchUIPreferences = (
  payload: UIPreferencesPatch
): Promise<ApiResponse<UIPreferencesResponse>> => {
  recentRead = null
  return request.api.patch<UIPreferencesResponse>(
    "/user/ui-preferences/",
    payload
  )
}
